<template>
  <AppLayout>
    <template #header>
      <PageHeadingWithActionsAndBreadcrumbs
        :title="title"
        :breadcrumbs="breadcrumbs"
      />
    </template>

    <form @submit.prevent="submit">
      <FormLayoutMultiColumnCard columns="grid-cols-6" gap="gap-4">
        <div class="col-span-6 sm:col-span-4">
          <InputGroup
            v-model="form.name"
            label="Name"
            placeholder="King Air 90"
            :error="form.errors.name"
          />
        </div>

        <div class="col-span-6 sm:col-span-2">
          <InputGroup
            v-model="form.tail_number"
            label="Tail Number"
            placeholder="N123AB"
            :error="form.errors.tail_number"
          />
        </div>

        <div class="col-span-6 sm:col-span-3">
          <LabelErrorContainer
            label="Home Airport"
            :error="form.errors['airport.id']"
          >
            <AirportPicker
              v-model="form.airport"
              placeholder="Search airports"
            />
          </LabelErrorContainer>
        </div>

        <div class="col-span-6 sm:col-span-3">
          <InputGroup
            v-model="form.average_flight_mph"
            type="number"
            label="Average Flight Speed (mph)"
            placeholder="250"
            :error="form.errors.average_flight_mph"
          />
          <InputHelpText>Used to estimate arrival times.</InputHelpText>
        </div>

        <div class="col-span-6 sm:col-span-3">
          <LabelErrorContainer
            label="Default Pilot"
            :error="form.errors['user.id']"
          >
            <UserPicker v-model="form.user" placeholder="Optional" />
          </LabelErrorContainer>
          <InputHelpText>
            Added as the pilot on every new trip.
            <LinkBase v-if="form.user" @click="form.user = null"
              >Clear</LinkBase
            >
          </InputHelpText>
        </div>

        <template #bottom>
          <ButtonBase type="submit" :processing="form.processing">{{
            props.plane ? 'Save' : 'Create Plane'
          }}</ButtonBase>
        </template>
      </FormLayoutMultiColumnCard>
    </form>
  </AppLayout>
</template>

<script setup>
import AppLayout from '@/Layouts/AppLayout.vue'
import PageHeadingWithActionsAndBreadcrumbs from '@/Common/Headings/PageHeadings/PageHeadingWithActionsAndBreadcrumbs.vue'
import FormLayoutMultiColumnCard from '@/Common/Forms/FormLayouts/FormLayoutMultiColumnCard.vue'
import InputGroup from '@/Common/Forms/Inputs/InputGroup.vue'
import InputHelpText from '@/Common/Forms/Inputs/InputHelpText.vue'
import LabelErrorContainer from '@/Common/Forms/Inputs/LabelErrorContainer.vue'
import ButtonBase from '@/Common/Elements/Buttons/ButtonBase.vue'
import LinkBase from '@/Common/Text/LinkBase.vue'
import AirportPicker from '../Trips/AirportPicker.vue'
import UserPicker from './UserPicker.vue'
import { useForm } from '@inertiajs/inertia-vue3'
import { computed } from 'vue'

const props = defineProps({
  plane: Object,
})

const title = computed(() =>
  props.plane ? `Edit ${props.plane.tail_number}` : 'New Plane'
)

const breadcrumbs = computed(() =>
  props.plane
    ? [
        {
          title: props.plane.tail_number,
          href: route('planes.show', props.plane),
        },
      ]
    : [{ title: 'Planes', href: route('planes.index') }]
)

const form = useForm({
  name: props.plane?.name ?? '',
  tail_number: props.plane?.tail_number ?? '',
  airport: props.plane?.airport ?? null,
  average_flight_mph: props.plane?.average_flight_mph ?? '',
  user: props.plane?.user ?? null,
})

function submit() {
  // The server only needs the ids of the picked airport and pilot
  form.transform((data) => ({
    ...data,
    airport: data.airport ? { id: data.airport.id } : null,
    user: data.user ? { id: data.user.id } : null,
  }))

  if (props.plane) form.put(route('planes.update', props.plane))
  else form.post(route('planes.store'))
}
</script>
